// ============================================================
// CMS Open Payments API Utility
//
// All data is served from BigQuery via the /api/bigquery Vercel serverless function.
// ============================================================

export const DATASET_YEARS = {
  2025: "GNRL_PGYR2025",
  2024: "GNRL_PGYR2024",
  2023: "GNRL_PGYR2023",
  2022: "GNRL_PGYR2022",
  2021: "GNRL_PGYR2021",
  2020: "GNRL_PGYR2020",
  2019: "GNRL_PGYR2019",
  2018: "GNRL_PGYR2018",
  2017: "GNRL_PGYR2017",
  2016: "GNRL_PGYR2016",
};

export const DEFAULT_YEAR = 2025;

const API_BASE = "/api/bigquery";

async function callApi(action, params) {
  const query = new URLSearchParams({ action, ...params }).toString();
  const res = await fetch(`${API_BASE}?${query}`);
  if (!res.ok) throw new Error("Failed to fetch from API");
  return res.json();
}

export async function searchPhysicians(firstName, lastName, year = DEFAULT_YEAR, filters = {}) {
  return callApi("searchPhysicians", {
    firstName: firstName || "",
    lastName: lastName || "",
    npi: filters.npi || "",
    state: filters.state !== "ALL" ? filters.state : "",
    specialty: filters.specialty !== "ALL" ? filters.specialty : "",
    excludedOnly: filters.excludedOnly ? "true" : "",
  });
}

export async function getPhysicianPayments(npi, year = DEFAULT_YEAR) {
  return callApi("getPhysicianPayments", { npi, year });
}

export async function getPaymentsByCompanyAllYears(npi) {
  const res = await fetch(
    `${API_BASE}?action=getPaymentsByCompanyAllYears&npi=${encodeURIComponent(npi)}`
  );
  if (!res.ok) throw new Error("Failed to fetch all-years company payments");
  return res.json();
}

export async function getPaymentsByCompany(npi, year = DEFAULT_YEAR) {
  const payments = await getPhysicianPayments(npi, year);

  const companyMap = {};
  for (const p of payments) {
    const key = p.companyName || "Unknown";
    if (!companyMap[key]) {
      companyMap[key] = {
        companyName: key,
        companyId: p.companyId || null,
        total: 0,
        count: 0,
        categories: {},
        products: new Set(),
        payments: [],
      };
    }
    companyMap[key].total += p.amount || 0;
    companyMap[key].count += 1;

    // Store the full payment object so all fields are available
    companyMap[key].payments.push({
      id: p.id,
      amount: p.amount,
      natureOfPayment: p.natureOfPayment,
      date: p.date,
      reportYear: p.reportYear,
      product1: p.product1 || null,
      productType1: p.productType1 || null,
      product2: p.product2 || null,
      productType2: p.productType2 || null,
      product3: p.product3 || null,
      productType3: p.productType3 || null,
      product4: p.product4 || null,
      productType4: p.productType4 || null,
      product5: p.product5 || null,
      productType5: p.productType5 || null,
    });

    const cat = p.natureOfPayment || "Other";
    companyMap[key].categories[cat] = (companyMap[key].categories[cat] || 0) + (p.amount || 0);

    // Capture all 5 product slots for the summary
    [p.product1, p.product2, p.product3, p.product4, p.product5]
      .filter((prod) => prod && prod.trim() !== "")
      .forEach((prod) => companyMap[key].products.add(prod));
  }

  return Object.values(companyMap)
    .map((c) => ({
      ...c,
      products: Array.from(c.products),
      payments: c.payments.sort((a, b) => new Date(b.date) - new Date(a.date)),
    }))
    .sort((a, b) => b.total - a.total);    
}

export async function searchByCompany(companyName, year = DEFAULT_YEAR, filters = {}) {
  return callApi("searchByCompany", {
    companyName,
    year,
    state: filters.state !== "ALL" ? filters.state : "",
    specialty: filters.specialty !== "ALL" ? filters.specialty : "",
    excludedOnly: filters.excludedOnly ? "true" : "",
    ...(filters.userId && filters.listIds ? { userId: filters.userId, listIds: filters.listIds } : {}),
  });
}

export async function checkExclusions(npi, firstName, lastName, state) {
  return callApi("checkExclusions", {
    npi: npi || "",
    firstName: firstName || "",
    lastName: lastName || "",
    state: state || "",
  });
}

export async function checkExclusionsBatch(npis) {
  if (!npis || npis.length === 0) return [];
  return callApi("checkExclusionsBatch", {
    npis: npis.join(","),
  });
}

export async function getMultiYearSummary(npi) {
  return callApi("getMultiYearSummary", { npi });
}

export async function searchCompanies(companyName, year = DEFAULT_YEAR) {
  return callApi("searchCompanies", { companyName, year });
}

export async function getOwnershipByNPI(npi) {
  return callApi("getOwnershipByNPI", { npi });
}

export async function getPhysicianPodFlags(npi) {
  return callApi("getPhysicianPodFlags", { npi });
}

export async function getPodRiskByCompany(companyId) {
  return callApi("getPodRiskByCompany", { companyId });
}

export async function getDualInterestByNPI(npi) {
  return callApi("getDualInterestByNPI", { npi });
}

export async function getOwnershipByCompany(companyName) {
  return callApi("getOwnershipByCompany", { companyName });
}

export async function getOwnershipByCompanyId(companyId, filterParams = {}) {
  return callApi("getOwnershipByCompany", {
    companyId,
    ...(filterParams.userId && filterParams.listIds ? { userId: filterParams.userId, listIds: filterParams.listIds } : {}),
  });
}

export async function searchOwnership(params = {}) {
  return callApi("searchOwnership", {
    npi: params.npi || "",
    firstName: params.firstName || "",
    lastName: params.lastName || "",
    state: params.state || "",
  });
}

export async function getOwnershipDetailByNPI(npi) {
  return callApi("getOwnershipDetailByNPI", { npi });
}

export async function checkPaymentsByCompanyBatch(npis, companyId) {
  return callApi("checkPaymentsByCompanyBatch", { npis: npis.join(","), companyId });
}

export async function checkResearchByCompanyBatch(npis, companyId) {
  return callApi("checkResearchByCompanyBatch", { npis: npis.join(","), companyId });
}

export async function getProviderFlags(npi) {
  return callApi("getProviderFlags", { npi });
}

export async function getProviderFromNPPES(npi) {
  const res = await fetch(`/api/nppes?npi=${encodeURIComponent(npi)}`);
  return res.json();
}

export function formatCurrency(amount) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(amount || 0);
}

export async function getGeneralPaymentsByNPIAndCompany(npi, companyIds) {
  return callApi("getGeneralPaymentsByNPIAndCompany", {
    npi,
    companyIds: companyIds.join(","),
  });
}

export async function checkPaymentsBatch(npis) {
  if (!npis || npis.length === 0) return [];
  return callApi("checkPaymentsBatch", { npis: npis.join(",") });
}

export async function checkOwnershipBatch(npis) {
  if (!npis || npis.length === 0) return [];
  return callApi("checkOwnershipBatch", { npis: npis.join(",") });
}

export async function checkResearchBatch(npis) {
  if (!npis || npis.length === 0) return [];
  return callApi("checkResearchBatch", { npis: npis.join(",") });
}

export async function getProviderInfo(npi) {
  return callApi("getProviderInfo", { npi });
}

export async function getResearchByNPI(npi) {
  return callApi("getResearchByNPI", { npi });
}

export async function getResearchDetailByNPI(npi) {
  return callApi("getResearchDetailByNPI", { npi });
}

export async function getDirectResearchByNPI(npi) {
  return callApi("getDirectResearchByNPI", { npi });
}

export async function getPiResearchByCompanyByYear(companyId) {
  return callApi("getPiResearchByCompanyByYear", { companyId });
}

export async function getDirectResearchByCompany(companyId, filterParams = {}) {
  return callApi("getDirectResearchByCompany", {
    companyId,
    ...(filterParams.userId && filterParams.listIds ? { userId: filterParams.userId, listIds: filterParams.listIds } : {}),
  });
}

export async function getDirectResearchByCompanyByYear(companyId) {
  return callApi("getDirectResearchByCompanyByYear", { companyId });
}

export async function getDirectResearchByCompanyFull(companyId) {
  return callApi("getDirectResearchByCompanyFull", { companyId });
}

export async function getDirectResearchByCompanyYearlyTotals(companyId) {
  return callApi("getDirectResearchByCompanyYearlyTotals", { companyId });
}

export async function getResearchOwnershipDualInterest(npi) {
  return callApi("getResearchOwnershipDualInterest", { npi });
}

export async function getResearchStudiesByNPI(npi) {
  return callApi("getResearchStudiesByNPI", { npi });
  
}

export async function getResearchYearCountByNPI(npi) {
  return callApi("getResearchYearCountByNPI", { npi });
}

export async function getResearchProductsByNPI(npi) {
  return callApi("getResearchProductsByNPI", { npi });
}

export async function getCompanyProfile(companyId) {
  return callApi("getCompanyProfile", { companyId });
}

export async function getProviderNamesBatch(npis) {
  if (!npis || npis.length === 0) return [];
  return callApi("getProviderNamesBatch", { npis: npis.join(",") });
}

export async function getResearchByCompany(companyId, filterParams = {}) {
  return callApi("getResearchByCompany", {
    companyId,
    ...(filterParams.userId && filterParams.listIds ? { userId: filterParams.userId, listIds: filterParams.listIds } : {}),
  });
}

export async function getResearchCountByCompany(companyId, filterParams = {}) {
  return callApi("getResearchCountByCompany", {
    companyId,
    ...(filterParams.userId && filterParams.listIds ? { userId: filterParams.userId, listIds: filterParams.listIds } : {}),
  });
}

export async function getResearchByCompanyFull(companyId, filterParams = {}) {
  return callApi("getResearchByCompanyFull", {
    companyId,
    ...(filterParams.userId && filterParams.listIds ? { userId: filterParams.userId, listIds: filterParams.listIds } : {}),
  });
}

export async function getResearchProductSummaryByCompany(companyId, filterParams = {}) {
  return callApi("getResearchProductSummaryByCompany", {
    companyId,
    ...(filterParams.userId && filterParams.listIds ? { userId: filterParams.userId, listIds: filterParams.listIds } : {}),
  });
}

export async function getResearchByCompanyByYear(companyId, filterParams = {}) {
  return callApi("getResearchByCompanyByYear", {
    companyId,
    ...(filterParams.userId && filterParams.listIds ? { userId: filterParams.userId, listIds: filterParams.listIds } : {}),
  });
}

export async function getResearchGeneralDualInterestByNPI(npi) {
  return callApi("getResearchGeneralDualInterestByNPI", { npi });
}

export async function checkDualInterestBatch(npis) {
  if (!npis || npis.length === 0) return [];
  return callApi("checkDualInterestBatch", { npis: npis.join(",") });
}

export async function checkPodBatch(npis) {
  if (!npis || npis.length === 0) return [];
  return callApi("checkPodBatch", { npis: npis.join(",") });
}

export async function checkPodRiskByCompanyBatch(companyIds) {
  if (!companyIds || companyIds.length === 0) return [];
  return callApi("checkPodRiskByCompanyBatch", { companyIds: companyIds.join(",") });
}

export async function getOwnershipDetailByCompany(companyId) {
  return callApi("getOwnershipDetailByCompany", { companyId });
}

export async function getCompanyAliases(companyId) {
  return callApi("getCompanyAliases", { companyId });
}

export async function getResearchGeneralDualInterestCountByCompany(companyId, filterParams = {}) {
  return callApi("getResearchGeneralDualInterestCountByCompany", {
    companyId,
    ...(filterParams.userId && filterParams.listIds ? { userId: filterParams.userId, listIds: filterParams.listIds } : {}),
  });
}

export async function getFilteredCompanyYearlyTotals(companyId, filterParams = {}) {
  if (!filterParams.userId || !filterParams.listIds) return [];
  return callApi("getFilteredCompanyYearlyTotals", {
    companyId,
    userId: filterParams.userId,
    listIds: filterParams.listIds,
  });
}

export async function getFilteredTopProducts(companyId, year, filterParams = {}) {
  if (!filterParams.userId || !filterParams.listIds) return [];
  return callApi("getFilteredTopProducts", {
    companyId,
    year,
    userId: filterParams.userId,
    listIds: filterParams.listIds,
  });
}

export async function getFilteredSpecialtyBreakdown(companyId, year, filterParams = {}) {
  if (!filterParams.userId || !filterParams.listIds) return [];
  return callApi("getFilteredSpecialtyBreakdown", {
    companyId,
    year,
    userId: filterParams.userId,
    listIds: filterParams.listIds,
  });
}

export async function getFilteredStateBreakdown(companyId, year, filterParams = {}) {
  if (!filterParams.userId || !filterParams.listIds) return [];
  return callApi("getFilteredStateBreakdown", {
    companyId,
    year,
    userId: filterParams.userId,
    listIds: filterParams.listIds,
  });
}

export async function getResearchProductSummaryByCompanyByYear(companyId, year, filterParams = {}) {
  return callApi("getResearchProductSummaryByCompanyByYear", {
    companyId,
    year,
    ...(filterParams.userId && filterParams.listIds ? { userId: filterParams.userId, listIds: filterParams.listIds } : {}),
  });
}

export async function getResearchStateBreakdownByCompany(companyId, year, filterParams = {}) {
  return callApi("getResearchStateBreakdownByCompany", {
    companyId,
    year: year || "all",
    ...filterParams,
  });
}

export async function getFilteredPaymentTypeBreakdown(companyId, year, filterParams = {}) {
  if (!filterParams.userId || !filterParams.listIds) return [];
  return callApi("getFilteredPaymentTypeBreakdown", {
    companyId,
    year,
    userId: filterParams.userId,
    listIds: filterParams.listIds,
  });
}

export async function getListSummary(userId, listId) {
  return callApi("getListSummary", { userId, listId });
}

export async function getListOwnershipSummary(userId, listId) {
  return callApi("getListOwnershipSummary", { userId, listId });
}

export async function getListResearchSummary(userId, listId, year) {
  return callApi("getListResearchSummary", { userId, listId, year: year || "all" });
}

export async function getListAllOwnershipCompanies(userId, listId) {
  return callApi("getListAllOwnershipCompanies", { userId, listId });
}

export async function getListOwnershipDetail(userId, listId) {
  return callApi("getListOwnershipDetail", { userId, listId });
}

export async function getListAllResearchCompanies(userId, listId, year) {
  return callApi("getListAllResearchCompanies", { userId, listId, year: year || "all" });
}

export async function getListResearchProviders(userId, listId, year) {
  return callApi("getListResearchProviders", { userId, listId, year: year || "all" });
}

export async function getListResearchProducts(userId, listId, year) {
  return callApi("getListResearchProducts", { userId, listId, year: year || "all" });
}

export async function getListGeneralStateBreakdown(userId, listId, year) {
  return callApi("getListGeneralStateBreakdown", { userId, listId, year: year || "all" });
}

export async function getListTopProducts(userId, listId, year) {
  return callApi("getListTopProducts", { userId, listId, year: year || "all" });
}

export async function getListOwnershipStateBreakdown(userId, listId) {
  return callApi("getListOwnershipStateBreakdown", { userId, listId });
}

export async function getListGeneralByYear(userId, listId, year) {
  return callApi("getListGeneralByYear", { userId, listId, year });
}

export async function getListCompanyTrend(userId, listId) {
  return callApi("getListCompanyTrend", { userId, listId });
}

export async function getListOwnershipTrend(userId, listId) {
  return callApi("getListOwnershipTrend", { userId, listId });
}

export async function getListResearchTrend(userId, listId) {
  return callApi("getListResearchTrend", { userId, listId });
}

export async function getListTopProviders(userId, listId, year) {
  return callApi("getListTopProviders", { userId, listId, year: year || "all" });
}

export async function getListAllCompanies(userId, listId, year) {
  return callApi("getListAllCompanies", { userId, listId, year: year || "all" });
}

export async function getPhysicianStatePayments(npi, year) {
  return callApi("getPhysicianStatePayments", { npi, year });
}

export async function getResearchStatePayments(npi, year) {
  return callApi("getResearchStatePayments", { npi, year });
}

export async function getCitiesByState(state, query) {
  const res = await fetch(
    `/api/build-list?action=getCitiesByState&state=${encodeURIComponent(state)}&query=${encodeURIComponent(query)}`
  );
  if (!res.ok) throw new Error("Failed to fetch cities");
  return res.json();
}
